import React, { useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import SwitchOption from 'v3/components/SwitchOption';
import Text from 'v3/components/Text';
import Card from 'v3/components/Card';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useParams } from 'react-router-dom';
import { FaClipboardList, FaEdit, FaPlus, FaSave, FaTimes } from 'react-icons/fa';
import { useClient } from '../context';
import OccurrenceTypeItem from './OccurrenceTypeItem';
import NewTypeModal from './Modal/NewTypeModal';
import { useModal } from 'hooks';
import {
  AddButton,
  TypesList,
  EmptyState,
} from './styles';
import { Button } from 'v3/components';

export default function OccurrenceTypes() {
  const client = useClient();
  const params = useParams();
  const newTypeModal = useModal();

  const [snapshot, setSnapshot] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const isCreateMode = !params?.id;
  const canEditOccurrenceTypes = isCreateMode || client.isEditing || isSaving;

  const occurrenceTypes = [...(client.data?.occurrenceClientTypes || [])].sort(
    (a, b) => (a?.order ?? 0) - (b?.order ?? 0)
  );

  const mandatoryOrder = !!client.data?.orderRequired;

  const handleToggleMandatoryOrder = (value) => {
    if (!canEditOccurrenceTypes) {
      return;
    }

    client.setData({ orderRequired: value });
  };

  const handleSaveNewType = (typeData) => {
    if (!canEditOccurrenceTypes) {
      return;
    }

    const nextTypes = Array.isArray(typeData) ? typeData : [typeData];
    const currentIds = new Set(occurrenceTypes.map((type) => type?.id));
    const uniqueTypes = nextTypes.filter((type) => !currentIds.has(type?.id));

    if (!uniqueTypes.length) {
      client.showSnackbar('Esse tipo de ocorrência já foi adicionado', { type: 'warning' });
      return;
    }

    if (uniqueTypes.length < nextTypes.length) {
      client.showSnackbar('Alguns tipos já existiam e não foram adicionados', { type: 'warning' });
    }

    const mergedTypes = [...occurrenceTypes, ...uniqueTypes].map((item, index) => ({
      ...item,
      order: index + 1,
    }));

    client.setData({ occurrenceClientTypes: mergedTypes });
  };

  const handleStartEdit = () => {
    setSnapshot({
      occurrenceTypes,
      orderRequired: mandatoryOrder,
    });
    client.setIsEditing(true);
  };

  const handleSaveEdit = async () => {
    if (!client.isEditing) {
      return;
    }

    setIsSaving(true);
    try {
      const orderedOccurrenceTypes = occurrenceTypes.map((item, index) => ({
        ...item,
        order: index + 1,
      }));

      client.setData({ occurrenceClientTypes: orderedOccurrenceTypes });

      await client.handleSaveClientOccurrenceTypes({
        occurrenceClientTypes: orderedOccurrenceTypes,
        orderRequired: mandatoryOrder,
      });

      client.setIsEditing(false);
      setSnapshot(null);
    } catch (error) {
      client.showSnackbar('Não foi possível salvar as alterações', { type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    const oldOccurrenceTypes = client.data?.occurrenceClientTypes || [];
    const oldOrderRequired = client.data?.orderRequired ?? false;

    if (snapshot) {
      client.setData({
        occurrenceClientTypes: snapshot.occurrenceTypes || oldOccurrenceTypes,
        orderRequired: snapshot.orderRequired ?? oldOrderRequired,
      });
    }
    client.setIsEditing(false);
    setSnapshot(null);
  };

  const handleDelete = (item) => {
    if (!canEditOccurrenceTypes) {
      return;
    }

    const filtered = occurrenceTypes.filter(type => type.id !== item.id);
    client.setData({ occurrenceClientTypes: filtered });
  };

  const handleUpdateFields = (updatedType) => {
    if (!canEditOccurrenceTypes) {
      return;
    }

    client.setData({
      occurrenceClientTypes: occurrenceTypes.map(t =>
        t.id === updatedType.id ? updatedType : t
      ),
    });
  };

  const handleDragEnd = (event) => {
    if (!canEditOccurrenceTypes) {
      return;
    }

    const { active, over } = event;

    if (active.id === over?.id) {
      return;
    }

    const oldIndex = occurrenceTypes.findIndex(t => t.id === active.id);
    const newIndex = occurrenceTypes.findIndex(t => t.id === over?.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const reordered = arrayMove(occurrenceTypes, oldIndex, newIndex);
      const withNewOrder = reordered.map((item, index) => ({
        ...item,
        order: index + 1,
      }));
      client.setData({ occurrenceClientTypes: withNewOrder });
    }
  };

  return (
    <Card
      loading={client.isFetching}
      bodyCardPadding='1rem 2rem'
      header={
        <Text type="header" color="dark">
          Tipos de Ocorrência
        </Text>
      }
      HeaderRightComponent={
        isCreateMode ? (
          <div style={{ display: 'flex', gap: '8px' }}>
            <AddButton onClick={newTypeModal.open} loading={isSaving}>
              <FaPlus />
              Incluir tipo ocorrência
            </AddButton>
          </div>
        ) : client.isEditing ? (
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between', width: '100%', marginTop: '5px' }}>
            <AddButton onClick={newTypeModal.open} loading={isSaving}>
              <FaPlus />
              Incluir tipo ocorrência
            </AddButton>
            <div style={{ display: 'flex', gap: '8px' }}>
              <Button
                onClick={handleCancelEdit}
                variant={'error'}
                style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                loading={isSaving}
              >
                <FaTimes />
                Cancelar
              </Button>
              <Button
                onClick={handleSaveEdit}
                loading={isSaving}
                disabled={isSaving}
                variant={'success'}
                style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                <FaSave />
                Salvar
              </Button>
            </div>
          </div>
        ) : (
          <Button variant="secondary" onClick={handleStartEdit} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <FaEdit />
            Editar
          </Button>
        )
      }
    >
      <Row className="form">
        <Col md={12}>
          <Col
            style={{
              height: '3rem',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              border: '1px solid',
              borderColor: '#838282',
              justifyContent: 'space-between',
              opacity: canEditOccurrenceTypes ? 1 : 0.6,
              pointerEvents: canEditOccurrenceTypes ? 'auto' : 'none',
            }}
          >
            <Text>
              Seguir ordenação obrigatória
            </Text>

            <SwitchOption
              activeText="Sim"
              inactiveText="Não"
              value={mandatoryOrder}
              loading={isSaving}
              onClick={() => handleToggleMandatoryOrder(!mandatoryOrder)}
            />

          </Col>

          {occurrenceTypes.length === 0 ? (
            <EmptyState>
              <FaClipboardList />
              <p>Nenhum tipo de ocorrência configurado</p>
            </EmptyState>
          ) : (
            <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={occurrenceTypes} strategy={verticalListSortingStrategy}>
                <TypesList>
                  {occurrenceTypes.map((item, index) => (
                    <OccurrenceTypeItem
                      key={item.id}
                      item={item}
                      index={index}
                      isEditing={canEditOccurrenceTypes}
                      onDelete={handleDelete}
                      onUpdateFields={handleUpdateFields}
                      isSaving={isSaving}
                    />
                  ))}
                </TypesList>
              </SortableContext>
            </DndContext>
          )}
          <NewTypeModal
            isOpen={newTypeModal.isOpen}
            onClose={newTypeModal.close}
            onSave={handleSaveNewType}
          />
        </Col>
      </Row>
    </Card>
  );
}


